/**
 * Lazy-loads <video class="lazy-video"> elements.
 *
 * Videos render with preload="none" and no autoplay attribute. This script
 * watches them with IntersectionObserver and, when they get within rootMargin
 * of the viewport, flips preload to "auto", calls load(), and triggers play()
 * for muted/looping background videos.
 *
 * Skips videos inside [data-lazy-video-managed] containers — those sections
 * (e.g. ztove-feature-spotlight) handle their own preload orchestration so
 * cycling through slides doesn't buffer.
 */

const LAZY_SELECTOR = 'video.lazy-video';
const ROOT_MARGIN = '0px 0px 800px 0px';

const isManaged = (video) => !!video.closest('[data-lazy-video-managed]');

const loadVideo = (video) => {
  if (video.dataset.lazyLoaded === 'true') return;
  video.dataset.lazyLoaded = 'true';
  video.preload = 'auto';
  video.load();

  const tryPlay = () => {
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  };

  if (video.readyState >= 2) {
    tryPlay();
  } else {
    video.addEventListener('loadeddata', tryPlay, { once: true });
  }
};

const observer =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            loadVideo(entry.target);
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: ROOT_MARGIN, threshold: 0 }
      )
    : null;

const observe = (video) => {
  if (isManaged(video)) return;
  if (!observer) {
    loadVideo(video);
    return;
  }
  observer.observe(video);
};

const scan = (root = document) => {
  root.querySelectorAll(LAZY_SELECTOR).forEach(observe);
};

scan();

// Catch videos added later (theme editor, section reloads, etc.)
const mo = new MutationObserver((mutations) => {
  mutations.forEach((m) => {
    m.addedNodes.forEach((node) => {
      if (node.nodeType !== 1) return;
      if (node.matches && node.matches(LAZY_SELECTOR)) observe(node);
      if (node.querySelectorAll) scan(node);
    });
  });
});
mo.observe(document.documentElement, { childList: true, subtree: true });
